import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';

import { SravniOrganizationsSharedOrganizationType } from '../../__generated__/news/Api';
import { Layout } from '../../components/Layouts/PageLayout';
import { LeftColumnContent } from '../../components/LeftColumn';
import { ListContent } from '../../components/ListContent';
import { ReduxBanner } from '../../components/ReduxBanner';
import SeoBlocks from '../../components/SeoBlocks';
import { SUBDOMAIN_RUBRICS_PARAMS } from '../../constants/news';
import { AB_NAME } from '../../constants/sitesettings';
import { sendChangeRouteSPAEvent } from '../../helpers/analytics';
import { loadMaterials } from '../../helpers/loadMaterials';
import { useOnFingerprintCreate } from '../../hooks/fingerprint';
import {
  getRubricName,
  getRubricType,
  getTagType,
  useListForUrl,
  useMaterialsList,
  useRubrics,
  useRubricType,
  useSingleMaterial,
  useTagType,
} from '../../hooks/news';
import { useFirstRender } from '../../hooks/useFirstReder';
import { useOrganizationInfoFromQuery } from '../../hooks/useOrganizationInfoFromQuery';
import { OrganizationsType } from '../../interfaces/menu';
import { IRouterQuery } from '../../interfaces/news';
import { fetchAdMaterialTagsAction, fetchBanners, fetchBannersWithStoreData } from '../../reducers/advertising';
import { fetchUserLikes } from '../../reducers/initialUserLikes';
import { fetchSeoIfNeeded } from '../../reducers/metadata';
import {
  fetchAllDictionariesIfNeeded,
  fetchAllPlacementsShortIfNeeded,
  fetchLatestNews,
  fetchRubricsListIfNeeded,
  fetchRubricsHasSubDomainListIfNeeded,
  setForListLoad,
} from '../../reducers/news';
import {
  fetchOrganizationByAlias,
  fetchOrganizationMenuIfNeeded,
  fetchOrganizationsInfoIfNeeded,
  fetchPartnersInfoIfNeeded,
} from '../../reducers/organizations';
import { useWhitelabel } from '../../reducers/whitelabel';
import { getAdMaterialTags } from '../../selectors/advertising';
import { getDictionaries, getMaterialsList, getRubrics } from '../../selectors/news';
import { getOrganizations } from '../../selectors/organizations';
import styles from '../../styles/common.module.scss';
import { App } from '../../types';
import { createApplicationError } from '../../utils/errorFabric';
import { isServer } from '../../utils/isServer';
import { scrollTo } from '../../utils/smoothScroll';
import { PageWithSubscriptionPopupProps } from '../_app';

let prevPath = null;

export const VersionContext = React.createContext(false);

const ListPage: NextPage<
  App.ReduxNextPageContext<IRouterQuery> & PageWithSubscriptionPopupProps & { fromServer: boolean },
  { isBVersion: boolean }
> = ({ subscriptionPopupState, fromServer }) => {
  const { events, asPath } = useRouter() as TRouter<IRouterQuery>;

  const dispatch = useDispatch();
  const isWhitelabel = useWhitelabel();
  const singleMaterial = useSingleMaterial();
  const listForLatestUrl = useListForUrl();
  const rubrics = useRubrics();
  const rubricType = useRubricType();
  const tagType = useTagType();

  const organization = useOrganizationInfoFromQuery();

  useEffect(() => {
    if (!fromServer && listForLatestUrl !== asPath) {
      //@ts-ignore
      dispatch(loadMaterials({ rubricsList: rubrics, rubricType, tagType, organizationId: organization?.id })).then(
        () => {
          dispatch(setForListLoad(asPath));
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  useOnFingerprintCreate((fingerPrint: string) => {
    if (!isWhitelabel) {
      dispatch(fetchUserLikes({ fingerPrint }));
    }
  });

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      const title = document.title || 'Новости';
      // prevent race condition between two events
      setTimeout(() => sendChangeRouteSPAEvent({ path, title }), 1000);
    };
    events.on('routeChangeComplete', handleRouteChange);

    if (singleMaterial && listForLatestUrl === asPath) {
      scrollTo(`[data-alias="${singleMaterial.alias}"]`, 'instant' as ScrollBehavior);
    }

    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const materialsList = useMaterialsList();
  const singleMaterialHideAds = materialsList[0]?.hideAds;
  const isFirstRender = useFirstRender();

  useEffect(() => {
    if (isFirstRender || !materialsList.length) return;

    dispatch(
      fetchBannersWithStoreData({
        params: {
          BannerTypes: [
            BANNER_TYPES.TOP,
            BANNER_TYPES.OUT_OF_PAGE,
            BANNER_TYPES.SIDE,
            BANNER_TYPES.TGBM_7,
            // @ts-ignore
            'teaserInPropositions',
          ],
          Category: 'none',
          Product: 'none',
          Page: 'news',
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleMaterialHideAds]);

  return (
    <Interceptor componentName="news-list-component">
      <Layout>
        <Layout.LeftSide>
          {!isWhitelabel && <LeftColumnContent subscriptionPopupState={subscriptionPopupState} />}
        </Layout.LeftSide>
        <Layout.Content>
          <VersionContext.Provider value={false}>
            <ListContent />
          </VersionContext.Provider>
        </Layout.Content>
        <Layout.Foot>
          <SeoBlocks className={styles.content} />
          <ReduxBanner farce type={BANNER_TYPES.OUT_OF_PAGE} abName={AB_NAME} />
        </Layout.Foot>
      </Layout>
    </Interceptor>
  );
};

ListPage.getInitialProps = async (ctx: App.ReduxNextPageContext<IRouterQuery>) => {
  const {
    asPath,
    query: { rubric, rubricAlias, tag, type, product, organizationId, organizationAlias },
    store: { dispatch, getState },
    req,
  } = ctx;

  const [pathname] = asPath.split('?');

  const statusCode = 404;
  const statusMessage = 'Not found';

  if (!req) {
    const preventExecuteOnTheSamePath = asPath === prevPath;
    if (preventExecuteOnTheSamePath) {
      return;
    }
    prevPath = asPath;
  }

  if (isServer) {
    dispatch(setForListLoad(asPath));
  }

  await Promise.all([
    dispatch(fetchAllPlacementsShortIfNeeded()),
    dispatch(fetchAllDictionariesIfNeeded()),
    dispatch(fetchAdMaterialTagsAction()),
    dispatch(fetchRubricsListIfNeeded()),
    dispatch(fetchRubricsHasSubDomainListIfNeeded(SUBDOMAIN_RUBRICS_PARAMS)),
  ]);

  const { rubrics: dictionaryRubrics, tags } = getDictionaries(getState());
  const rubricsList = getRubrics(getState());

  const rubricType = getRubricType({ type, dictionaryRubrics, rubric, rubricAlias, product, organizationAlias });
  const rubricName = getRubricName({ type, dictionaryRubrics, rubric, rubricAlias, product });
  const tagType = getTagType({ tag, tags });
  const isPartnerNews = Boolean(organizationAlias) || rubricType === 'novosti-partnerov';

  const isInvalidPath = pathname.includes('/teg/');
  const magPathError = !isInvalidPath && !rubricType && pathname !== '/mag/';

  if ((isInvalidPath && !tagType) || magPathError) throw createApplicationError(statusCode, statusMessage);

  const promises = [];

  let organization;

  if (organizationId || organizationAlias) {
    await dispatch(
      fetchOrganizationsInfoIfNeeded({
        activeOnly: false,
      }),
    );

    const organizations = Object.values(getOrganizations(getState()));

    organization =
      organizationId || organizationAlias
        ? organizations.find((item) => item.id === organizationId || item.alias === organizationAlias)
        : null;

    if (organization) {
      await dispatch(
        fetchOrganizationByAlias({
          alias: organization.alias,
          type: organization.type,
        }),
      );

      const hasMenu = organization.type !== SravniOrganizationsSharedOrganizationType.Other;

      if (hasMenu) {
        promises.push(
          dispatch(
            fetchOrganizationMenuIfNeeded({
              type: organization.type === 'insurance' ? OrganizationsType.INSURANCE_COMPANY : organization.type,
              id: organization.id,
              alias: organization.alias,
              location: '6.',
              status: organization.status,
            }),
          ),
        );
      }
    } else {
      throw createApplicationError(statusCode, statusMessage);
    }
  }

  if (isServer) {
    // @ts-ignore
    promises.push(dispatch(loadMaterials({ rubricsList, rubricType, tagType, organizationId: organization?.id })));
  }

  promises.push(dispatch(fetchLatestNews()));

  if (isPartnerNews) {
    promises.push(
      dispatch(
        fetchPartnersInfoIfNeeded({
          take: 6,
          activityDays: 360,
        }),
      ),
    );
  }

  await Promise.all(promises);

  if (isServer) {
    const materialsList = getMaterialsList(getState());
    const adTags = getAdMaterialTags(getState());

    await dispatch(
      fetchBanners({
        params: {
          BannerTypes: [BANNER_TYPES.OUT_OF_PAGE, BANNER_TYPES.SIDE, BANNER_TYPES.TGBM_7, BANNER_TYPES.TOP],
          Category: 'none',
          Product: 'none',
          Page: 'news',
        },
        source: materialsList?.[0],
        adTags,
      }),
    );
  }

  // @ts-ignore
  await dispatch(fetchSeoIfNeeded(organization, pathname, { rubricName, rubricType, tagType, pageType: 'list' }));

  const isBVersion = isServer && ctx.req.cookies?.['AB_NEWS_DIRECT'] === 'always';

  return {
    isBVersion,
    fromServer: isServer,
  };
};

export default ListPage;
